import { LinearProgress } from '@mui/material'
import Box from '@mui/material/Box'
import Pagination from '@mui/material/Pagination'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import React, { forwardRef, memo, type ReactElement, useCallback, useMemo } from 'react'
import { type TableComponents, TableVirtuoso } from 'react-virtuoso'
import KNDataTableRow from './KNDataTableRow'
import type { KNDataTableAction, KNDataTableProps } from './types'

const getScroller =
  forwardRef<HTMLDivElement, React.ComponentProps<typeof TableContainer>>(
    (props, ref) => <TableContainer {...props} ref={ref} />,
  )

const getTableHead =
  forwardRef<HTMLTableSectionElement>(
    (props, ref) => <TableHead {...props} ref={ref} />
  )

const getTableBody =
  forwardRef<HTMLTableSectionElement>(
    (props, ref) => <TableBody {...props} ref={ref} />
  )

const KNDataTable = <T extends object>({
  tableRef,
  columns,
  actions,
  data,
  page = 0,
  pageCount = 1,
  useWindowScroll = true,
  onRowClick,
  onPageChange,
  overscan = 300,
  isLoading,
  sx,
  tableSx,
}: KNDataTableProps<T>): ReactElement => {
  const primaryActions: KNDataTableAction<T>[] = actions?.filter((action) => !action.secondary) ?? []
  const secondaryActions: KNDataTableAction<T>[] = actions?.filter((action) => action.secondary) ?? []

  const getTable = (props) => (
    <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed', ...tableSx }} />
  )

  const getTableRow = ({ item: row, ...props }) => (
    <TableRow
      {...props}
      hover={true}
      onClick={() => {
        onRowClick?.(row)
      }}
      sx={
        onRowClick && {
          cursor: 'pointer',
        }
      }
      className="hoverable"
    />
  )

  const VirtuosoTableComponents: TableComponents<T> = useMemo(
    () => ({
      Scroller: getScroller,
      Table: getTable,
      TableHead: getTableHead,
      TableRow: getTableRow,
      TableBody: getTableBody,
    }),
    [],
  )

  const fixedHeaderContent = useCallback(() => {
    // calculate fixed width of actions column
    const actionsWidth = 2 + ((primaryActions.length ?? 0) + ((secondaryActions.length ?? 0) > 0 ? 1 : 0)) * 2
    return (
      <TableRow>
        {columns.map((column) => (
          <TableCell key={column.name} sx={column.sx}>
            {column.label}
          </TableCell>
        ))}
        {(primaryActions || secondaryActions) && <TableCell sx={{ width: `${actionsWidth}rem` }} />}
      </TableRow>
    )
  }, [columns, actions])

  const itemContent = useCallback(
    (_index: number, row: T) => (
      <KNDataTableRow columns={columns} row={row} primaryActions={primaryActions} secondaryActions={secondaryActions} />
    ),
    [columns, actions],
  )

  const handlePageChange = useCallback((event: React.ChangeEvent<unknown>, page: number) => {
    onPageChange?.(page - 1) // NOTE: backend pagination is 0-index based
  }, [])

  return (
    <Box sx={{ position: 'relative', ...sx }} data-guide="datatable">
      {isLoading && (
        <LinearProgress sx={{ position: 'absolute', top: '-4px', left: 0, right: 0 }} />
      )}
      <TableVirtuoso
        ref={tableRef}
        data={data}
        components={VirtuosoTableComponents}
        fixedHeaderContent={fixedHeaderContent}
        itemContent={itemContent}
        useWindowScroll={useWindowScroll}
        overscan={overscan}
      />

      {pageCount > 1 && (
        <Stack direction="row" justifyContent="center" mt={2}>
          <Pagination
            variant="outlined"
            shape="rounded"
            color="secondary"
            count={pageCount}
            page={page + 1} // NOTE: backend pagination is 0-index based
            onChange={handlePageChange}
            disabled={isLoading}
          />
        </Stack>
      )}
    </Box>
  )
}

// NOTE: additional syntax for memoizing component with generic props
// https://github.com/DefinitelyTyped/DefinitelyTyped/issues/37087#issuecomment-656596623
export default memo(KNDataTable) as typeof KNDataTable
